import type { JSX } from 'react';
import { cn } from '../../../lib/cn.js';
import { resolveAssetUrl } from './image-source.js';

export interface CompareItem {
  src: string;
  alt: string;
  label?: string;
  caption?: string;
  transparent?: boolean;
}

export interface CompareProps {
  items: CompareItem[];
  columns?: 2 | 3;
  caption?: string;
  className?: string;
}

// Side-by-side comparison panels (e.g. low / medium / high quality, or style A
// vs B). Each panel has an optional mono label strip and caption.
export function Compare({ items, columns = 2, caption, className }: CompareProps): JSX.Element {
  return (
    <figure className={cn('my-5', className)}>
      <div
        className={cn(
          'grid grid-cols-1 gap-4',
          columns === 3 ? 'standard:grid-cols-3' : 'standard:grid-cols-2',
        )}
      >
        {items.map((item, i) => (
          <div key={i} className="overflow-hidden rounded-lg border border-white/[0.06]">
            {item.label ? (
              <div className="border-b border-white/[0.06] bg-white/[0.02] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
                {item.label}
              </div>
            ) : null}
            <img
              src={resolveAssetUrl(item.src)}
              alt={item.alt}
              loading="lazy"
              decoding="async"
              className={cn('block w-full', item.transparent && 'checkerboard')}
            />
            {item.caption ? (
              <div className="px-3 py-2 text-[13px] leading-[1.5] text-secondary">
                {item.caption}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {caption ? (
        <figcaption className="mt-2.5 text-center text-[13px] leading-[1.5] text-muted">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}
